// composeEmailModal — single source of truth for the "email this inventory"
// modal, assembled from its section slots. Both the live configurator
// (SceneDesignerTool's modalRenderOverrides["pdf-email"]) and the focus-mode
// section preview (FocusHost, focus=section:email) call this so the two render
// the same modal — header + form.
//
// Slots:
//   modal:pdf-email-header — text blocks: the first heading block is the title,
//                            the first body block is the subtitle.
//   modal:pdf-email        — the form (input-* blocks ending in a form-submit).

import { Slot } from "../blocks/Slot";
import type { BlockHostContext, BlockInstance, SlotMapping } from "../blocks/types";

export interface ComposeEmailModalArgs {
  /** Fully-resolved slot mapping (layout default < saved override < live overlay). */
  mapping: SlotMapping;
  host: BlockHostContext;
  onClose: () => void;
  /** Used only when the header slot has no title / subtitle text at all. */
  fallbackTitle?: string;
  fallbackSubtitle?: string;
}

function textOf(b: BlockInstance | undefined): string | undefined {
  const t = b && (b.props as Record<string, unknown> | undefined)?.text;
  return typeof t === "string" && t.trim() ? t : undefined;
}

function variantOf(b: BlockInstance): string | undefined {
  const v = (b.props as Record<string, unknown> | undefined)?.variant;
  return typeof v === "string" ? v : undefined;
}

export function composeEmailModal(args: ComposeEmailModalArgs): React.ReactElement {
  const { mapping, host, onClose, fallbackTitle, fallbackSubtitle } = args;

  const header = mapping["modal:pdf-email-header"] ?? [];
  const bodyEntries = mapping["modal:pdf-email"] ?? [];

  // The header's first heading text block is the title; its first body block is
  // the subtitle. "heading" covers the heading variants (heading / heading-*).
  const titleBlock = header.find(b => b.block === "text" && (variantOf(b)?.startsWith("heading") ?? false));
  const subtitleBlock = header.find(b => b.block === "text" && b !== titleBlock);

  const title = textOf(titleBlock) || fallbackTitle || "Send Project Inventory";
  const subtitle = textOf(subtitleBlock) || fallbackSubtitle || "";

  return (
    <div className="sd-modal__inner sd-modal__inner--pdf-email">
      <header className="sd-modal__header">
        <div className="sd-pdfemail__heading">
          <h2 className="sd-pdfemail__title">{title}</h2>
          {subtitle && <p className="sd-pdfemail__subtitle">{subtitle}</p>}
        </div>
        <button type="button" className="sd-modal__close" onClick={onClose} aria-label="Close">×</button>
      </header>
      <div className="sd-modal__body">
        <Slot slotId="modal:pdf-email" mapping={bodyEntries} host={host} inline />
      </div>
    </div>
  );
}
